import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, User } from 'lucide-react';
import { COUNCIL_LEVELS } from '../../mock';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LEVEL_LABELS = {
  state: 'State Level', district: 'District Level', taluk: 'Taluk Level',
  gram_panchayat: 'Gram Panchayat Level', general: 'General Members',
};
const COUNCIL_FIELDS = [
  { key: 'designation', label: 'Designation' }, { key: 'intro', label: 'Introduction', area: true },
  { key: 'achievements', label: 'Achievements', area: true }, { key: 'qualifications', label: 'Qualifications', area: true },
  { key: 'awards', label: 'Awards & Recognition', area: true }, { key: 'contributions', label: 'Contributions', area: true },
  { key: 'contact', label: 'Contact' },
];
const GENERAL_FIELDS = [
  { key: 'contact_number', label: 'Contact Number' }, { key: 'location', label: 'Location' },
  { key: 'email', label: 'Email' }, { key: 'place_of_stay', label: 'Place of Stay' },
];
const emptyForm = (level) => ({
  level, name: '', designation: '', intro: '', achievements: '', qualifications: '', awards: '', contributions: '',
  contact: '', contact_number: '', location: '', email: '', place_of_stay: '', order: 0,
});

export const MemberFormDialog = ({ member, defaultLevel, token, onUnauthorized, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (member ? { ...emptyForm(member.level), ...member } : emptyForm(defaultLevel)));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const auth = { Authorization: `Bearer ${token}` };
  const fields = form.level === 'general' ? GENERAL_FIELDS : COUNCIL_FIELDS;

  const fail = (e, msg) => { if (e?.response?.status === 401) onUnauthorized(); else if (msg) window.alert(e?.response?.data?.detail || msg); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/upload-photo`, fd, { headers: { ...auth, 'Content-Type': 'multipart/form-data' } });
      setF('photo', res.data.photo);
    } catch (e) { fail(e, 'Photo upload failed'); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (member) await axios.put(`${API}/admin/members/${member.id}`, form, { headers: auth });
      else await axios.post(`${API}/admin/members`, form, { headers: auth });
      onSaved(form.level);
    } catch (e) { fail(e, 'Save failed'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="jbss-admin-form-dialog" data-testid="admin-form-dialog">
        <DialogHeader>
          <DialogTitle>{member ? 'Edit Member' : 'Add Member'}</DialogTitle>
          <DialogDescription>Fill in the member details and save.</DialogDescription>
        </DialogHeader>
        <div className="jbss-admin-form">
          <div className="jbss-admin-field">
            <Label>Photo</Label>
            <div className="jbss-photo-upload">
              {form.photo ? <img src={`${API}/photos/${form.photo}`} alt="" className="jbss-photo-preview" data-testid="admin-photo-preview" />
                : <div className="jbss-photo-placeholder-sm"><User size={26} /></div>}
              <div className="jbss-photo-upload-actions">
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => uploadPhoto(e.target.files && e.target.files[0])} data-testid="admin-photo-input" />
                {uploading && <span className="jbss-photo-hint"><Loader2 className="jbss-spin" size={14} /> Uploading...</span>}
                {form.photo && !uploading && <button type="button" className="jbss-admin-link" onClick={() => setF('photo', '')} data-testid="admin-photo-remove">Remove photo</button>}
              </div>
            </div>
          </div>
          <div className="jbss-admin-field">
            <Label>Level</Label>
            <Select value={form.level} onValueChange={(v) => setF('level', v)}>
              <SelectTrigger data-testid="admin-form-level"><SelectValue /></SelectTrigger>
              <SelectContent>{COUNCIL_LEVELS.map((lv) => <SelectItem key={lv} value={lv}>{LEVEL_LABELS[lv]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="jbss-admin-field">
            <Label htmlFor="f-name">Name *</Label>
            <Input id="f-name" value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="admin-form-name" />
          </div>
          {fields.map((fd) => (
            <div className="jbss-admin-field" key={fd.key}>
              <Label htmlFor={`f-${fd.key}`}>{fd.label}</Label>
              {fd.area
                ? <Textarea id={`f-${fd.key}`} value={form[fd.key] || ''} onChange={(e) => setF(fd.key, e.target.value)} data-testid={`admin-form-${fd.key}`} />
                : <Input id={`f-${fd.key}`} value={form[fd.key] || ''} onChange={(e) => setF(fd.key, e.target.value)} data-testid={`admin-form-${fd.key}`} />}
            </div>
          ))}
          <div className="jbss-admin-field">
            <Label htmlFor="f-order">Display Order</Label>
            <Input id="f-order" type="number" value={form.order} onChange={(e) => setF('order', parseInt(e.target.value || '0', 10))} data-testid="admin-form-order" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name.trim()} data-testid="admin-form-save">
            {saving ? <Loader2 className="jbss-spin" size={16} /> : null} {member ? 'Save Changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
